/**
 * Dashboard Page Template
 *
 * Finance dashboard showing KPIs, charts, and activity feed.
 * Uses the controller pattern: useDashboardController() handles data,
 * this component handles only rendering.
 *
 * Charts use ECharts via the chart component library.
 * Wrapped in ChartProvider for automatic theme bridging.
 */

"use client";

import React from "react";
import { useDashboardController } from "./useDashboardController";

// ─── Library imports ─────────────────────────────────────────────
import { PageContainer } from "@/src/components/layout/page-container";
import { Section } from "@/src/components/layout/section";
import { Text } from "@/src/components/information/text";
import { Badge } from "@/src/components/information/badge";
import { KPICard } from "@/src/components/information/kpi-card";
import { Button } from "@/src/components/input/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/src/components/container/card";
import { SectionCard } from "@/src/components/container/section-card";
import DashboardGrid, { DashboardGridItem } from "@/src/components/charts/dashboard-grid";

// ─── Chart components (ECharts-powered) ──────────────────────────
import { ChartProvider } from "@/src/components/charts/chart-provider";
import { BarChart } from "@/src/components/charts/bar-chart";
import { DonutChart } from "@/src/components/charts/donut-chart";
import { AreaChart } from "@/src/components/charts/area-chart";
import { GaugeChart } from "@/src/components/charts/gauge-chart";

// ─── Motion ───────────────────────────────────────────────────────
import { useCountUp } from "@/src/utils/motion-utils";

// ─── Icons ─────────────────────────────────────────────────────────
import { RefreshCw } from "lucide-react";

// ─── Helpers ───────────────────────────────────────────────────────

const activityConfig: Record<string, { color: string; icon: string }> = {
  approve: { color: "text-success", icon: "✓" },
  reject: { color: "text-destructive", icon: "✕" },
  create: { color: "text-info", icon: "+" },
  update: { color: "text-warning", icon: "⟳" },
  comment: { color: "text-purple-500", icon: "✎" },
  system: { color: "text-muted-foreground", icon: "⚙" },
};

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ─── Animated KPI wrapper (uses useCountUp hook) ──────────────────

interface AnimatedKPIProps {
  kpi: ReturnType<typeof useDashboardController>["kpis"][number];
}

function AnimatedKPI({ kpi }: AnimatedKPIProps) {
  // Parse the formatted value to determine prefix/suffix/decimals
  const isPercent = kpi.formattedValue.endsWith("%");
  const isCurrency = kpi.formattedValue.startsWith("$");
  const isMillion = kpi.formattedValue.endsWith("M");

  // For currency like "$2.85M", animate the numeric part
  const displayTarget = isMillion
    ? kpi.value / 1_000_000
    : isPercent
    ? kpi.value
    : kpi.value;

  const animatedValue = useCountUp(displayTarget, {
    duration: 1200,
    decimals: isMillion ? 2 : 0,
    prefix: isCurrency ? "$" : "",
    suffix: isMillion ? "M" : isPercent ? "%" : "",
  });

  return (
    <DashboardGridItem key={kpi.id}>
      <KPICard
        label={kpi.label}
        value={animatedValue}
        description={kpi.changeLabel}
        descriptionTrailing={
          <Text
            as="span"
            className={`text-xs font-medium ${
              kpi.trend === "up"
                ? "text-success"
                : kpi.trend === "down"
                ? "text-destructive"
                : "text-muted-foreground"
            }`}
          >
            {kpi.trend === "up" ? "↑" : kpi.trend === "down" ? "↓" : "→"}{" "}
            {Math.abs(kpi.change)}%
          </Text>
        }
      />
    </DashboardGridItem>
  );
}

// ─── Dashboard Page ────────────────────────────────────────────────

export default function DashboardPage() {
  const ctrl = useDashboardController();

  return (
    <ChartProvider>
    <PageContainer scrollable className="!px-0 !py-0 space-y-theme-6 h-full">
      {/* ─── Header ─── */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <Text as="h1" className="text-h2 font-bold">
            Financial Dashboard
          </Text>
          <Text as="p" className="text-body text-muted-foreground mt-theme-2">
            Real-time overview of your financial operations
          </Text>
        </div>
        <div className="flex items-center gap-theme-3">
          <Badge variant="outline">
            <Text as="span" className="text-xs">
              Last updated: {new Date().toLocaleTimeString()}
            </Text>
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={ctrl.refresh}
            disabled={ctrl.loading}
            leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* ─── KPI Row ─── */}
      <DashboardGrid columns={4} gap="md" animated>
        {ctrl.kpis.map((kpi) => (
          <AnimatedKPI key={kpi.id} kpi={kpi} />
        ))}
      </DashboardGrid>

      {/* ─── Charts Row 1: Revenue + Expense Breakdown ─── */}
      <DashboardGrid columns={3} gap="md" animated>
        <DashboardGridItem span={2}>
          <SectionCard
            header={<CardTitle>Revenue vs Expenses</CardTitle>}
            contentClassName="p-theme-5"
          >
            <BarChart
              data={ctrl.revenueExpenseChart}
              height={320}
              showLegend
            />
          </SectionCard>
        </DashboardGridItem>

        <DashboardGridItem>
          <SectionCard
            header={<CardTitle>Expense Breakdown</CardTitle>}
            contentClassName="p-theme-5"
          >
            <DonutChart
              data={ctrl.expenseBreakdown}
              height={320}
              centerText="$1.92M"
              centerSubText="Total"
            />
          </SectionCard>
        </DashboardGridItem>
      </DashboardGrid>

      {/* ─── Charts Row 2: Cash Flow + Gauge + Activity ─── */}
      <DashboardGrid columns={3} gap="md" animated>
        <DashboardGridItem>
          <SectionCard
            header={<CardTitle>Cash Flow Trend</CardTitle>}
          >
            <AreaChart
              data={ctrl.cashFlowChart}
              height={260}
              smooth
            />
          </SectionCard>
        </DashboardGridItem>

        <DashboardGridItem>
          <SectionCard
            header={<CardTitle>Close Completion</CardTitle>}
            contentClassName="flex items-center justify-center"
          >
            <GaugeChart
              value={87}
              max={100}
              height={260}
              suffix="%"
              label="Month-End Close"
            />
          </SectionCard>
        </DashboardGridItem>

        <DashboardGridItem>
          <SectionCard
            header={<CardTitle>Recent Activity</CardTitle>}
            headerClassName="px-theme-5 py-theme-4"
            className="!px-0 !py-0 divide-y"
          >
            <div className="divide-y divide-border max-h-[300px] overflow-auto stagger-children">
              {ctrl.activities.map((activity) => {
                const config = activityConfig[activity.type] ?? activityConfig.system;
                return (
                  <div
                    key={activity.id}
                    className="px-theme-5 py-theme-4 flex items-start gap-theme-3 hover:bg-accent/40 transition-colors"
                  >
                    <div
                      className={`mt-0.5 text-sm font-bold shrink-0 w-5 text-center ${config.color}`}
                    >
                      {config.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Text as="p" className="text-sm leading-snug">
                        {activity.message}
                      </Text>
                      <Text as="p" className="text-xs text-muted-foreground mt-0.5">
                        {activity.user} · {timeAgo(activity.timestamp)}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </DashboardGridItem>
      </DashboardGrid>
    </PageContainer>
    </ChartProvider>
  );
}

export { DashboardPage };
